import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  MenuItem,
} from "@mui/material";
import deliveryAgentsApi from "../../api/delivery-agents";

interface DeliveryAgent {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  aadharNumber?: string;
  bikeNumber?: string;
  agentType?: string;
  instantStatus?: string;
  jobStartTime?: string;
}

interface EditDeliveryAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  deliveryAgent: DeliveryAgent | null;
}

const EditDeliveryAgentDialog: React.FC<EditDeliveryAgentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  deliveryAgent,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    isActive: true,
    aadharNumber: "",
    bikeNumber: "",
    agentType: "subscription",
    instantStatus: "OFF_DUTY",
    jobStartTime: "05:00 AM",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (deliveryAgent) {
      setFormData({
        name: deliveryAgent.name || "",
        email: deliveryAgent.email || "",
        phoneNumber: deliveryAgent.phoneNumber || "",
        isActive: deliveryAgent.isActive,
        aadharNumber: deliveryAgent.aadharNumber || "",
        bikeNumber: deliveryAgent.bikeNumber || "",
        agentType: deliveryAgent.agentType || "subscription",
        instantStatus: deliveryAgent.instantStatus || "OFF_DUTY",
        jobStartTime: deliveryAgent.jobStartTime || "05:00 AM",
      });
    }
  }, [deliveryAgent]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deliveryAgent) return;

    setLoading(true);
    setError(null);

    try {
      await deliveryAgentsApi.updateDeliveryAgent(deliveryAgent._id, formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || "Error updating delivery agent");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Delivery Agent</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              fullWidth
            />
            <TextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Aadhar Number"
              value={formData.aadharNumber}
              onChange={(e) => handleInputChange("aadharNumber", e.target.value)}
              fullWidth
            />
            <TextField
              label="Bike Number"
              value={formData.bikeNumber}
              onChange={(e) => handleInputChange("bikeNumber", e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Agent Type"
              value={formData.agentType}
              onChange={(e) => handleInputChange("agentType", e.target.value)}
              fullWidth
            >
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="instant">Instant</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </TextField>
            <TextField
              select
              label="Instant Status"
              value={formData.instantStatus}
              onChange={(e) => handleInputChange("instantStatus", e.target.value)}
              fullWidth
            >
              <MenuItem value="AVAILABLE">AVAILABLE</MenuItem>
              <MenuItem value="ON_DELIVERY">ON DELIVERY</MenuItem>
              <MenuItem value="OFF_DUTY">OFF DUTY</MenuItem>
            </TextField>
            <TextField
              label="Default Start Time (e.g. 05:00 AM)"
              value={formData.jobStartTime}
              onChange={(e) => handleInputChange("jobStartTime", e.target.value)}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditDeliveryAgentDialog; 